import { useEffect, useRef, useState } from "react";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatUKDate } from "@/lib/date";
import { useSlugScope } from "@/lib/scoped";
import { toast } from "sonner";
import { Users, Download, TrendingUp, UserCheck, UserPlus, Layers as LayersIcon, ShieldCheck, AlertTriangle, ChevronDown, CheckCircle2, Circle } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import RedemptionLogCard from "@/components/admin/RedemptionLogCard";
import StripeTransactionsCard from "@/components/admin/StripeTransactionsCard";
import PerformanceByOrgCard from "@/components/admin/PerformanceByOrgCard";

const SECTION = "border border-zinc-200 rounded-sm bg-white";
const CHART_MARGIN = { top: 8, right: 8, left: -16, bottom: 8 };
const AXIS_TICK = { fontSize: 11, fill: "#71717A" };
const TOOLTIP_CURSOR_BLUE = { fill: "rgba(0,56,255,0.06)" };
const TOOLTIP_CONTENT = { borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 };
const BAR_RADIUS = [2, 2, 0, 0];

function shortDate(iso) {
  try {
    return formatUKDate(iso, { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, headers, rows) {
  const lines = [headers.map(csvEscape).join(","), ...rows.map((r) => r.map(csvEscape).join(","))];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function MetricTile({ item, loading }) {
  const Icon = item.icon;
  return (
    <div className={`${SECTION} p-5`} data-testid={item.testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {item.label}
      </p>
      <p
        className="font-heading text-3xl font-bold tabular-nums text-zinc-950 mt-2"
        data-testid={`${item.testId}-value`}
      >
        {loading ? "…" : item.value}
      </p>
      <p className="text-[11px] text-zinc-500 mt-1">{item.helper}</p>
    </div>
  );
}

// iter153e — Collapsible "Who's predicted this gameweek" list. Rendered
// below the voucher summary on `/c/<slug>/key-monitors`. Reads from
// GET /editor/predictors?scope_slug=<slug>, which returns users with a
// prediction on any FUTURE match (match_date >= today) plus their paid
// league_entry status for the active season.
function WhoPredictedCard({ scopeSlug = null }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const qs = scopeSlug ? `?scope_slug=${encodeURIComponent(scopeSlug)}` : "";
        const { data } = await api.get(`/editor/predictors${qs}`);
        if (ignore) return;
        setRows(Array.isArray(data?.predictors) ? data.predictors : []);
      } catch (e) {
        if (!ignore) setError(formatApiError(e));
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [scopeSlug]);

  const total = rows?.length || 0;
  const paidCount = (rows || []).filter((r) => r.paid).length;

  return (
    <details
      className={`${SECTION} group`}
      data-testid="who-predicted-card"
    >
      <summary
        className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-sm"
        data-testid="who-predicted-toggle"
      >
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5">
            <UserCheck className="h-3 w-3" /> Who&apos;s predicted this gameweek
          </p>
          <p className="text-sm text-zinc-500 mt-1">
            {loading
              ? "Loading…"
              : total === 0
              ? "No predictions submitted yet"
              : `${total} player${total === 1 ? "" : "s"} · ${paidCount} paid · ${total - paidCount} unpaid`}
          </p>
        </div>
        <ChevronDown className="h-4 w-4 text-zinc-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t border-zinc-200 px-5 py-4">
        {error ? (
          <p className="text-sm text-red-600" data-testid="who-predicted-error">
            {error}
          </p>
        ) : loading ? (
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-400">Loading…</p>
        ) : total === 0 ? (
          <p className="text-sm text-zinc-500">
            No player on this pub has submitted a prediction for any upcoming match yet.
          </p>
        ) : (
          <ul className="divide-y divide-zinc-100" data-testid="who-predicted-list">
            {rows.map((r) => (
              <li
                key={r.user_id}
                className="flex items-center justify-between gap-3 py-2"
                data-testid={`who-predicted-row-${r.user_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-950 truncate" title={r.email}>
                    {r.email || r.name || r.user_id}
                  </p>
                  {r.name && r.email ? (
                    <p className="text-[11px] text-zinc-500 truncate">{r.name}</p>
                  ) : null}
                </div>
                <p className="text-[11px] text-zinc-400 tabular-nums">
                  {r.predictions_count} pick{r.predictions_count === 1 ? "" : "s"}
                </p>
                {r.paid ? (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-0.5"
                    data-testid={`who-predicted-row-${r.user_id}-paid`}
                  >
                    <CheckCircle2 className="h-3 w-3" /> Paid
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-0.5"
                    data-testid={`who-predicted-row-${r.user_id}-unpaid`}
                  >
                    <Circle className="h-3 w-3" /> Unpaid
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

function UserMetricsTable({ scopeSlug = null }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const suffix = scopeSlug ? `&scope_slug=${encodeURIComponent(scopeSlug)}` : "";
        const { data } = await api.get(`/editor/user-metrics?weeks=12${suffix}`);
        if (!cancelled) setMetrics(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeSlug]);

  const fmtNum = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-GB"));
  const fmtDec = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(1));
  const retention = metrics?.retention_rate;

  const primary = [
    {
      key: "registered",
      label: "Registered users",
      icon: Users,
      value: fmtNum(metrics?.registered_users),
      helper: "Total pub members on the roster",
      testId: "metric-registered",
    },
    {
      key: "active",
      label: "Active users",
      icon: UserCheck,
      value: fmtNum(metrics?.active_users),
      helper: "Registered users with ≥1 prediction ever",
      testId: "metric-active",
    },
    {
      key: "retention",
      label: "Retention rate",
      icon: TrendingUp,
      value:
        retention === null || retention === undefined
          ? "—"
          : `${(Number(retention) * 100).toFixed(0)}%`,
      helper: "Active ÷ registered users",
      testId: "metric-retention",
    },
  ];
  const secondary = [
    {
      key: "signups",
      label: "Signups this week",
      icon: UserPlus,
      value: fmtNum(metrics?.signups_last_7d),
      helper: "New registrations in the last 7 days",
      testId: "metric-signups",
    },
    {
      key: "depth",
      label: "Prediction depth",
      icon: LayersIcon,
      value: fmtDec(metrics?.avg_predictions_per_matchday),
      helper: "Average entries per match-day (last 12)",
      testId: "metric-depth",
    },
  ];

  return (
    <section data-testid="key-monitors-user-metrics" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="user-metrics-row-1">
        {primary.map((item) => <MetricTile key={item.key} item={item} loading={loading} />)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="user-metrics-row-2">
        {secondary.map((item) => <MetricTile key={item.key} item={item} loading={loading} />)}
      </div>
    </section>
  );
}

function EntrantsPerMatchDayCard({ scopeSlug = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const suffix = scopeSlug ? `&scope_slug=${encodeURIComponent(scopeSlug)}` : "";
        const { data } = await api.get(`/editor/stats?weeks=12${suffix}`);
        if (!cancelled) {
          const prepped = (data?.data || []).map((r) => ({ ...r, dateLabel: shortDate(r.date) }));
          setRows(prepped);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeSlug]);

  const isEmpty = rows.length === 0;

  return (
    <div className={`${SECTION} p-5`} data-testid="chart-entrants">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5">
          <Users className="h-3 w-3" /> Entrants per match-day
        </p>
        <div className="inline-flex items-center gap-3">
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">Last 12 weeks</p>
          <button
            type="button"
            onClick={() => {
              if (isEmpty) return;
              downloadCsv(
                `entrants-last-12-weeks.csv`,
                ["date", "entrants"],
                rows.map((r) => [r.date, r.entrants]),
              );
            }}
            data-testid="export-entrants-csv"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-zinc-200 text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950 hover:border-zinc-400 transition-colors"
          >
            <Download className="h-3 w-3" /> Export CSV
          </button>
        </div>
      </div>
      {loading ? (
        <p className="text-sm text-zinc-500 py-12 text-center">Loading…</p>
      ) : isEmpty ? (
        <p className="text-sm text-zinc-500 py-12 text-center">
          No data yet. Add fixtures and predictions to populate this chart.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={rows} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
            <XAxis dataKey="dateLabel" tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip cursor={TOOLTIP_CURSOR_BLUE} contentStyle={TOOLTIP_CONTENT} />
            <Bar dataKey="entrants" fill="#0038FF" radius={BAR_RADIUS} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function EmailBlockAuditCard() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [dryRunEmail, setDryRunEmail] = useState("");
  const [dryRun, setDryRun] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-block-audit");
      setAudit(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const testOne = async () => {
    if (!dryRunEmail.trim()) return;
    setDryRunLoading(true);
    setDryRun(null);
    try {
      const { data } = await api.post("/admin/email-block-audit/dry-run", { to: dryRunEmail.trim() });
      setDryRun(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDryRunLoading(false);
    }
  };

  const filtered = (audit?.rows || []).filter((r) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      (r.email || "").toLowerCase().includes(q) ||
      (r.name || "").toLowerCase().includes(q)
    );
  });

  const verdictColor = {
    "sent-admin": "text-emerald-700 bg-emerald-50 border-emerald-300",
    "sent-tenant-user": "text-amber-800 bg-amber-50 border-amber-300",
    "skipped-bot": "text-zinc-600 bg-zinc-100 border-zinc-300",
    "blocked-master": "text-red-700 bg-red-50 border-red-300",
  };

  return (
    <div className={`${SECTION} p-5`} data-testid="email-block-audit-card">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" /> Email block audit
          </p>
          <p className="text-sm text-zinc-600 mt-1 max-w-xl">
            For every registered user, shows whether they would receive a
            real email right now. Reveals master-only leaks in one click.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={loading}
          data-testid="email-block-audit-run"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-zinc-950 text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? "Running…" : audit ? "Refresh" : "Run audit"}
        </button>
      </div>

      {!audit ? (
        <p className="text-xs text-zinc-500">Click Run audit to fetch the live verdict for every user.</p>
      ) : null}

      <div className="mt-4 mb-4 p-3 border border-zinc-200 rounded-sm bg-zinc-50" data-testid="email-dry-run-panel">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">
          Test a specific email
        </p>
        <div className="flex gap-2 flex-wrap">
          <input
            type="email"
            value={dryRunEmail}
            onChange={(e) => setDryRunEmail(e.target.value)}
            placeholder="email@example.co.uk"
            data-testid="email-dry-run-input"
            className="flex-1 min-w-[200px] h-10 rounded-sm border border-zinc-200 bg-white px-3 text-sm"
          />
          <button
            type="button"
            onClick={testOne}
            disabled={dryRunLoading || !dryRunEmail.trim()}
            data-testid="email-dry-run-submit"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-zinc-950 text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-zinc-800 disabled:opacity-50"
          >
            {dryRunLoading ? "Testing…" : "Test"}
          </button>
        </div>
        {dryRun && (
          <div
            className={`mt-3 p-3 rounded-sm border text-xs space-y-1 ${
              dryRun.would_send_real
                ? "border-red-300 bg-red-50 text-red-900"
                : "border-emerald-300 bg-emerald-50 text-emerald-900"
            }`}
            data-testid="email-dry-run-result"
          >
            <p className="font-bold uppercase tracking-widest">
              {dryRun.would_send_real ? "WOULD SEND (LEAK)" : "BLOCKED / MOCKED"}
            </p>
            <p><strong>Reason:</strong> {dryRun.reason}</p>
            <p><strong>RESEND_MOCK:</strong> {String(dryRun.resend_mock)}</p>
            <p><strong>bot_guard fired:</strong> {String(dryRun.bot_guard)}</p>
            <p><strong>master_guard kept:</strong> {JSON.stringify(dryRun.master_guard_kept)}</p>
          </div>
        )}
      </div>

      {audit ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {["total", "sent-admin", "sent-tenant-user", "blocked-master", "skipped-bot"].map((k) => (
              <div
                key={k}
                className="border border-zinc-200 rounded-sm p-3"
                data-testid={`email-audit-total-${k}`}
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{k.replace(/-/g, " ")}</p>
                <p className="font-heading text-2xl font-bold tabular-nums text-zinc-950 mt-1">
                  {audit.totals?.[k] ?? 0}
                </p>
              </div>
            ))}
          </div>

          <div className="mb-3">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by email or name…"
              data-testid="email-audit-filter"
              className="w-full h-10 rounded-sm border border-zinc-200 bg-white px-3 text-sm"
            />
          </div>

          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="w-full text-xs" data-testid="email-audit-table">
              <thead className="bg-zinc-50">
                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Verdict</th>
                  <th className="px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.slice(0, 500).map((r) => (
                  <tr key={r.email} data-testid={`email-audit-row-${r.email}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-950 whitespace-nowrap">{r.email}</td>
                    <td className="px-3 py-2 text-zinc-800">{r.name || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-widest ${verdictColor[r.verdict] || ""}`}>
                        {r.verdict}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-zinc-600">{r.reason}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td className="px-3 py-6 text-center text-zinc-500" colSpan={4}>No matches.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filtered.length > 500 && (
            <p className="text-[11px] text-zinc-500 mt-2">Showing first 500 of {filtered.length} matches — refine your filter.</p>
          )}
        </>
      ) : null}
    </div>
  );
}

function AdminOnlyEmailAudit() {
  const { user } = useAuth();
  if (user?.role !== "admin") return null;
  return <EmailBlockAuditCard />;
}

function StripeWebhookWarningBanner() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/payments/health");
        if (!cancelled) setHealth(data);
      } catch {
        // Silent — banner just won't render if we can't reach the endpoint.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  if (user?.role !== "admin") return null;
  if (!health?.webhook_warning) return null;

  const keyLabel = health.is_live_key ? "live" : "test (BYOK)";
  return (
    <div
      role="alert"
      data-testid="stripe-webhook-warning-banner"
      className="border border-red-300 bg-red-50 text-red-900 rounded-sm p-4 flex items-start gap-3"
    >
      <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-red-600" aria-hidden="true" />
      <div className="text-sm leading-relaxed">
        <p className="font-bold">
          Stripe webhook secret missing — payments will not confirm reliably.
        </p>
        <p className="mt-1">
          A {keyLabel} Stripe key is configured, but{" "}
          <code className="px-1 bg-red-100 rounded-sm text-[12px]">STRIPE_WEBHOOK_SECRET</code>{" "}
          is unset. Incoming webhooks are being logged as{" "}
          <code className="px-1 bg-red-100 rounded-sm text-[12px]">[STRIPE_WEBHOOK_UNVERIFIED]</code>{" "}
          and skipped. Add the secret from the Stripe dashboard → Developers → Webhooks, then redeploy.
        </p>
      </div>
    </div>
  );
}

/**
 * Key Monitors — cross-organisation ops dashboard for Editors + Admins.
 * Currently hosts the "Entrants per match-day" chart and the "Redemption
 * log" table that used to live on the Editor page. More monitors can be
 * added here over time without cluttering the Editor surface.
 */
export default function KeyMonitorsPage() {
  // iter152 — slug-scope on `/c/<slug>/key-monitors`. Locks the Stripe
  // card and both data-loading cards below to a single tenant so a
  // super-admin browsing dugsy-salford's monitors doesn't see hq-salford
  // or global platform totals bleeding through.
  const scopeSlug = useSlugScope();
  // Shared filter — clicking a row in the scoreboard sets this, and the
  // Stripe transactions card below reacts by fetching just that pub's
  // transactions and scrolling into view. Also lets the Stripe card show
  // its own dropdown for admins who arrive on this page and want to
  // pivot without the scoreboard round-trip.
  const [filterPubSlug, setFilterPubSlug] = useState(scopeSlug || "");
  const [pubOptions, setPubOptions] = useState([]);
  const stripeCardRef = useRef(null);

  const handleSelectPub = (slug) => {
    setFilterPubSlug(slug || "");
    // Small timeout so the fetch kicks off first, then we scroll.
    window.setTimeout(() => {
      if (stripeCardRef.current) {
        stripeCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 60);
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main
        className="max-w-6xl mx-auto px-6 sm:px-10 py-8 sm:py-10 space-y-8"
        data-testid="key-monitors-page"
      >
        <header className="pb-4 border-b border-zinc-200">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            Ops
          </p>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Key monitors.
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-xl">
            Cross-organisation health checks — entrants per match-day and the
            live voucher summary. Editors and Super-Admins only.
          </p>
        </header>

        <StripeWebhookWarningBanner />

        <EntrantsPerMatchDayCard scopeSlug={scopeSlug} />

        <UserMetricsTable scopeSlug={scopeSlug} />

        <div className={`${SECTION} p-5`} data-testid="key-monitors-voucher-summary-wrap">
          <RedemptionLogCard scopeSlug={scopeSlug} />
          <WhoPredictedCard scopeSlug={scopeSlug} />
        </div>

        <AdminOnlyEmailAudit />

        <div className={SECTION} data-testid="key-monitors-performance-wrap">
          <PerformanceByOrgCard
            selectedSlug={filterPubSlug}
            onSelectPub={handleSelectPub}
            onPubsLoaded={setPubOptions}
          />
        </div>

        <div
          ref={stripeCardRef}
          className={SECTION}
          data-testid="key-monitors-stripe-txs-wrap"
        >
          <StripeTransactionsCard
            pubSlug={filterPubSlug}
            onPubSlugChange={setFilterPubSlug}
            pubOptions={pubOptions}
          />
        </div>
      </main>
    </div>
  );
}
